/*
  Repeats the header delivery bar's message in the bag drawer and, on a product
  page, in the Free Delivery box.

  Shopify cannot price delivery until it knows the address, so the old line
  ("shipping calculated at checkout") told the shopper nothing. The bar in the
  header already works out whether the basket has won free next day delivery,
  or how much is left to spend — this copies whichever of those it is
  currently showing, so the two lines can never disagree and the wording only
  has to be edited in one place, in the bar's own theme settings.
*/
(function waitForParts() {
  const states = document.querySelector('.shipping-bar-states');

  // The bar is rendered by the header group, but its script runs async, so on
  // a fast page this can land first. Try again on the next frame.
  if (!states) {
    requestAnimationFrame(waitForParts);
    return;
  }

  const STATES = [
    { name: 'default', selector: '.shipping-bar-default' },
    { name: 'progress', selector: '.shipping-bar-progress' },
    { name: 'qualified', selector: '.shipping-bar-qualified' },
  ];

  const MODIFIERS = STATES.map((state) => 'product__delivery-note--' + state.name);

  // Put on the panel around the note, not the note itself, because the panel's
  // heading changes once free delivery is won and CSS cannot reach backwards
  // up the markup from the note to the heading above it.
  const PANEL_WON = 'custom-deliveryinfo-box--qualified';
  // Something in the basket but not yet enough: the panel shows how much more
  // to spend, in the bar's own words, in place of its usual threshold line.
  const PANEL_PROGRESS = 'custom-deliveryinfo-box--progress';

  function visibleMessage() {
    for (const { name, selector } of STATES) {
      const state = states.querySelector(selector);
      // The bar hides the two states it is not using with Bootstrap's d-none.
      if (!state || state.classList.contains('d-none')) continue;

      // Copy so the bar's own markup is left alone. The bar's illustrations
      // belong to the strip across the top of the site; down here every state
      // is a plain line of text, so they are dropped.
      const copy = state.cloneNode(true);
      // The bag page keeps them: its note is drawn as a small copy of the bar.
      const withIcons = copy.innerHTML;
      copy.querySelectorAll('img').forEach((icon) => icon.remove());

      const text = copy.textContent.replace(/\s+/g, ' ').trim();
      // While the basket is being re-counted the amount is briefly empty.
      return text ? { name: name, html: copy.innerHTML, withIcons: withIcons } : null;
    }
    return null;
  }

  function sync() {
    const message = visibleMessage();

    // Looked up fresh each time: the bag drawer redraws its contents whenever
    // the bag changes, which brings a new, empty note with it.
    document.querySelectorAll('.product__delivery-note').forEach((note) => {
      // The state is put on the note as a class, and on the panel around it,
      // so each can be worded and laid out to suit where it sits.
      const panel = note.closest('.custom-deliveryinfo-box');
      note.classList.remove(...MODIFIERS);

      if (!message) {
        note.hidden = true;
        if (panel) panel.classList.remove(PANEL_WON, PANEL_PROGRESS);
        return;
      }

      // Only written when it has changed, so watching the drawer for changes
      // below cannot set off a loop of rewrites.
      const html = note.hasAttribute('data-keep-icons') ? message.withIcons : message.html;
      if (note.innerHTML !== html) note.innerHTML = html;
      note.classList.add('product__delivery-note--' + message.name);
      note.hidden = false;
      if (panel) {
        panel.classList.toggle(PANEL_WON, message.name === 'qualified');
        panel.classList.toggle(PANEL_PROGRESS, message.name === 'progress');
      }
    });
  }

  sync();

  // Adding to the basket from this page publishes the theme's cart update
  // event, which the header bar does not listen for - it waits for the cart
  // drawer to change. Nudge it to re-read the basket straight away, so the bar
  // and this panel both move the moment something is added.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      document.dispatchEvent(new Event('cart:refresh'));
    });
  }

  // The bag drawer replaces its footer each time the bag changes.
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(sync).observe(drawer, { childList: true, subtree: true });
  }

  // The bag page redraws its items (where its banner sits) and total the same way.
  document.querySelectorAll('#main-cart-footer, cart-items').forEach((part) => {
    new MutationObserver(sync).observe(part, { childList: true, subtree: true });
  });

  // The bar swaps state by changing classes, and fills the amount as text, so
  // watch for both rather than re-reading the basket ourselves.
  new MutationObserver(sync).observe(states, {
    attributes: true,
    attributeFilter: ['class'],
    characterData: true,
    childList: true,
    subtree: true,
  });
})();
